import { useMemo } from "react";
import { useQueryParams } from "@/hooks/use-query-param";
import { SESSION_ID_QUERY_PARAM } from "@/pages/app/provider/scheduling/group-appointment";
import { useGroupSessionById } from "@/queries/group-visit/use-group-session-by-id";
import { useGroupVisitById } from "@/queries/group-visit/use-group-visit-by-id";
import {
  CHECK_IN_DEFAULT_SESSION_NUMBER,
  CHECK_IN_LABEL,
} from "../group-visit-constant";
import {
  CheckInPatient,
  CheckInSession,
  GroupDetail,
} from "../group-visit-types";
import {
  toCheckInPatient,
  toGroupDetailFromGroupVisit,
} from "../group-visit-utils";

export type UseGroupCheckIn = {
  session: CheckInSession;
  group: GroupDetail;
  patients: CheckInPatient[];
  isLoading: boolean;
  isRefreshing: boolean;
  /** "Check-In — <group> — Session <n>" for the header bar. */
  title: string;
};

/**
 * Check-in state for one group session. The session (time and roster with
 * payments) comes from the session record named in the URL; the care team
 * comes from the group it belongs to.
 *
 * @author Rajkumar Rathod
 */
export const useGroupCheckIn = (): UseGroupCheckIn => {
  const sessionId = useQueryParams(SESSION_ID_QUERY_PARAM) ?? undefined;

  const {
    data: sessionDetail,
    isLoading: isSessionLoading,
    isFetching: isSessionFetching,
  } = useGroupSessionById(sessionId);
  const { data: savedGroup, isLoading: isGroupLoading } = useGroupVisitById(
    sessionDetail?.groupVisitId
  );

  const group = useMemo<GroupDetail>(
    () => (savedGroup ? toGroupDetailFromGroupVisit(savedGroup) : {}),
    [savedGroup]
  );

  const session = useMemo<CheckInSession>(
    () => ({
      uuid: sessionDetail?.uuid,
      groupName: sessionDetail?.groupName ?? group.groupName,
      startsAt: sessionDetail?.startTime,
      therapistName:
        sessionDetail?.therapistName ??
        sessionDetail?.primaryTherapistName ??
        group.primaryTherapistName,
      therapistId:
        sessionDetail?.primaryTherapistId ??
        sessionDetail?.therapistId ??
        undefined,
      sessionNumber: CHECK_IN_DEFAULT_SESSION_NUMBER,
      status: sessionDetail?.sessionStatus,
    }),
    [sessionDetail, group]
  );

  const patients = useMemo<CheckInPatient[]>(
    () => (sessionDetail?.patients ?? []).map(toCheckInPatient),
    [sessionDetail]
  );

  const title = useMemo(
    () =>
      [
        CHECK_IN_LABEL.title,
        session.groupName,
        `${CHECK_IN_LABEL.session} ${session.sessionNumber}`,
      ]
        .filter(Boolean)
        .join(CHECK_IN_LABEL.titleSeparator),
    [session]
  );

  return {
    session,
    group,
    patients,
    isLoading: isSessionLoading || isGroupLoading,
    isRefreshing: isSessionFetching,
    title,
  };
};
